// src/pages/AdminBillingPage.js
// GAAF Admin — view all companies payment status, record manual payments
import React, { useState, useEffect, useCallback } from 'react';
import { billingAPI } from '../services/api';
import { Card, Badge, SectionHeader, Button, Modal, Input, Select, EmptyState } from '../components/ui';
import toast from 'react-hot-toast';
import { DollarSign, AlertTriangle, CheckCircle, Clock, Building2, Search } from 'lucide-react';
import styles from './AdminBillingPage.module.css';

export default function AdminBillingPage() {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [manualTarget, setManualTarget] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await billingAPI.adminOverview();
            setCompanies(res.data.companies || []);
        } catch (_) { toast.error('Failed to load Billing Overview'); }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    // Filter before splitting into sections
    const filteredCompanies = companies.filter(c =>
        !search ||
        c.name?.toLowerCase().includes(search.toLowerCase()) ||
        c.plan?.toLowerCase().includes(search.toLowerCase()) ||
        c.status?.toLowerCase().includes(search.toLowerCase()) ||
        c.contactEmail?.toLowerCase().includes(search.toLowerCase()) ||
        c.country?.toLowerCase().includes(search.toLowerCase())
    );

    const pastDue = filteredCompanies.filter(c => c.status === 'past_due' || c.status === 'none');
    const active = filteredCompanies.filter(c => c.status === 'active');

    // Summary always uses full unfiltered list
    const allActive = companies.filter(c => c.status === 'active');
    const allPastDue = companies.filter(c => c.status === 'past_due' || c.status === 'none');

    return (
        <div className={`${styles.page} animate-fadeIn`}>
            <SectionHeader title="Billing Overview" description="Monitor Subscription Status for all GAAFrfid Clients" />

            <div className={styles.statsRow}>
                <Card className={styles.statCard}>
                    <CheckCircle size={20} style={{ color: 'var(--green-400)' }} />
                    <div className={styles.statNum}>{allActive.length}</div>
                    <div className={styles.statLabel}>Active</div>
                </Card>
                <Card className={styles.statCard}>
                    <AlertTriangle size={20} style={{ color: 'var(--warning)' }} />
                    <div className={styles.statNum}>{allPastDue.length}</div>
                    <div className={styles.statLabel}>Past Due / No Plan</div>
                </Card>
                <Card className={styles.statCard}>
                    <DollarSign size={20} style={{ color: 'var(--green-400)' }} />
                    <div className={styles.statNum}>
                        ${allActive.reduce((sum, c) => {
                            const prices = { starter: 399, growth: 799, enterprise: 1499 };
                            return sum + (prices[c.plan] || 0);
                        }, 0).toLocaleString()}
                    </div>
                    <div className={styles.statLabel}>MRR</div>
                </Card>
                <Card className={styles.statCard}>
                    <Building2 size={20} style={{ color: 'var(--text-tertiary)' }} />
                    <div className={styles.statNum}>{companies.length}</div>
                    <div className={styles.statLabel}>Total Companies</div>
                </Card>
            </div>

            <div style={{ position: 'relative', maxWidth: 360, marginBottom: 20 }}>
                <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', pointerEvents: 'none' }} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by Name, Plan, Status, Email…"
                    style={{ width: '100%', paddingLeft: 34, paddingRight: 12, paddingTop: 8, paddingBottom: 8, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', color: 'var(--text-primary)', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' }} />
            </div>

            {pastDue.length > 0 && (
                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}><AlertTriangle size={16} /> Needs Attention ({pastDue.length})</h2>
                    <div className={styles.companyList}>
                        {pastDue.map(c => <CompanyRow key={c.companyId} company={c} onRecord={() => setManualTarget(c)} />)}
                    </div>
                </div>
            )}

            {active.length > 0 && (
                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>Active ({active.length})</h2>
                    <div className={styles.companyList}>
                        {active.map(c => <CompanyRow key={c.companyId} company={c} onRecord={() => setManualTarget(c)} />)}
                    </div>
                </div>
            )}

            {!loading && filteredCompanies.length === 0 && (
                <EmptyState icon={<Building2 size={40} />}
                    title={search ? 'No Companies match your search' : 'No Companies'}
                    description={search ? 'Try a different Name, Plan, or Status.' : 'Add Companies to see Billing Status here.'} />
            )}

            <ManualPaymentModal open={!!manualTarget} company={manualTarget}
                onClose={() => setManualTarget(null)} onSaved={() => { setManualTarget(null); load(); }} />
        </div>
    );
}

function CompanyRow({ company, onRecord }) {
    const statusVariant = { active: 'success', past_due: 'danger', none: 'neutral', canceled: 'neutral' }[company.status] || 'neutral';
    const planLabel = { starter: 'Starter — $399/mo', growth: 'Growth — $799/mo', enterprise: 'Enterprise — $1499/mo' }[company.plan] || 'No Plan';

    return (
        <Card className={styles.companyRow}>
            <div className={styles.companyLeft}>
                <div className={styles.companyAvatar}>{(company.name || '?')[0].toUpperCase()}</div>
                <div>
                    <div className={styles.companyName}>{company.name}</div>
                    <div className={styles.companyMeta}>
                        {company.contactEmail && <span>{company.contactEmail}</span>}
                        {company.country && <span>📍 {company.country}</span>}
                    </div>
                </div>
            </div>
            <div className={styles.companyRight}>
                <div className={styles.planInfo}>
                    <span className={styles.planName}>{planLabel}</span>
                    {company.source && <span className={styles.planSource}>{company.source === 'stripe' ? '💳 Stripe' : '🏦 Manual'}</span>}
                    {company.paidUntil && <span className={styles.paidUntil}><Clock size={11} /> Until {new Date(company.paidUntil).toLocaleDateString()}</span>}
                </div>
                <Badge variant={statusVariant}>{company.status || 'none'}</Badge>
                <Button variant="secondary" size="sm" onClick={onRecord}>Record Payment</Button>
            </div>
        </Card>
    );
}

function ManualPaymentModal({ open, company, onClose, onSaved }) {
    const [form, setForm] = useState({ plan: 'starter', amount: '', notes: '', paidUntil: '' });
    const [loading, setLoading] = useState(false);
    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    useEffect(() => {
        if (open && company) {
            const next = new Date();
            next.setMonth(next.getMonth() + 1);
            setForm({
                plan: company.plan || 'starter',
                amount: company.plan === 'growth' ? '799' : company.plan === 'enterprise' ? '1499' : '399',
                notes: '',
                paidUntil: next.toISOString().split('T')[0],
            });
        }
    }, [open, company]);

    const handleSave = async () => {
        if (!form.plan) return toast.error('Select a Plan');
        setLoading(true);
        try {
            await billingAPI.manual({ companyId: company.companyId, plan: form.plan, amount: parseFloat(form.amount) || null, notes: form.notes, paidUntil: form.paidUntil || null });
            toast.success(`Payment recorded for ${company.name}`);
            onSaved();
        } catch (err) { toast.error(err?.response?.data?.message || 'Failed to record payment'); }
        setLoading(false);
    };

    return (
        <Modal open={open} onClose={onClose} title={`Record Payment — ${company?.name}`} size="md">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className={styles.manualNote}>Use this Form to Activate a Plan for Clients who paid by Wire Transfer or Check. This does not charge anything — it only updates their access in the system.</div>
                <Select label="Plan *" value={form.plan} onChange={e => { const prices = { starter: '399', growth: '799', enterprise: '1499' }; set('plan', e.target.value); set('amount', prices[e.target.value] || ''); }}>
                    <option value="starter">Starter — $399/mo — 1 Warehouse</option>
                    <option value="growth">Growth — $799/mo — 3 Warehouses</option>
                    <option value="enterprise">Enterprise — $1499/mo — Unlimited</option>
                </Select>
                <Input label="Amount Received (USD)" type="number" value={form.amount} onChange={e => set('amount', e.target.value)} placeholder="199" hint="For your records only" />
                <Input label="Paid Until *" type="date" value={form.paidUntil} onChange={e => set('paidUntil', e.target.value)} hint="System will show past_due after this date if not renewed" />
                <Input label="Notes" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Wire Transfer Reference #, Check Number, etc." />
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 8, borderTop: '1px solid var(--border-subtle)' }}>
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" loading={loading} onClick={handleSave}>Activate Plan</Button>
                </div>
            </div>
        </Modal>
    );
}
